import { useEffect, useState } from 'react'
import { api } from '../api/client'

/**
 * Die Einstellungen des eigenen Betriebs.
 *
 * Getrennt von der Seite „Parameter": Die gilt plattformweit und gehört dem Betreiber.
 * Was hier steht, entscheidet der Kunde für sich — ob im Betriebshof ein Aushang gescannt
 * werden muss, ist eine Frage des Betriebsablaufs, nicht der Plattform.
 */
interface ParameterAntwort {
  schluessel: string
  wert: string
  typ: string
  bezeichnung: string
  beschreibung: string
  vorgabe: string
  /** Kleinster erlaubter Wert bei ZAHL — 0 dort, wo „kein Ablauf" gemeint sein darf. */
  minimum: number
}

/** Die Parameter nach ihrem Schlüsselanfang — sonst stünde alles unter einer Überschrift. */
const GRUPPEN: { praefix: string; titel: string; fuss?: string }[] = [
  {
    praefix: 'anwesenheit.',
    titel: 'Anwesenheit auf dem Betriebshof',
    fuss:
      'Verlangt der Betrieb den Scan, holt die App den Scanner von sich aus nach vorn. ' +
      'Eine Meldung ohne Scan wird trotzdem gebucht — sie gilt dann als nicht ' +
      'nachgewiesen und ist in der Liste „Gerade im Haus" orange gekennzeichnet.',
  },
  { praefix: 'kopplung.', titel: 'Kopplung der Fahrer-App' },
  { praefix: 'verkehrsmeldung.', titel: 'Verkehrsmeldungen' },
  // Zuletzt: Was keiner Gruppe zugeordnet ist, erscheint hier statt gar nicht.
  { praefix: '', titel: 'Weitere Einstellungen' },
]

function zuGruppe(schluessel: string) {
  return GRUPPEN.find((g) => schluessel.startsWith(g.praefix))!
}

export function Betriebseinstellungen() {
  const [parameter, setParameter] = useState<ParameterAntwort[]>([])
  const [entwuerfe, setEntwuerfe] = useState<Record<string, string>>({})
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)

  async function laden() {
    setLaedt(true)
    try {
      const daten = await api.get<ParameterAntwort[]>('/api/betrieb/parameter')
      setParameter(daten)
      setEntwuerfe(Object.fromEntries(daten.map((p) => [p.schluessel, p.wert])))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function speichern(p: ParameterAntwort) {
    try {
      const neu = await api.put<ParameterAntwort>(`/api/betrieb/parameter/${p.schluessel}`, {
        wert: entwuerfe[p.schluessel],
      })
      setParameter((alt) => alt.map((x) => (x.schluessel === p.schluessel ? neu : x)))
      setMeldung({ art: 'erfolg', text: `„${p.bezeichnung}" gespeichert` })
    } catch (ex) {
      setMeldung({
        art: 'fehler',
        text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen',
      })
    }
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Betriebseinstellungen</h1>
          <p>Regeln, die nur für diesen Betrieb gelten</p>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {laedt ? (
        <div className="laden">Wird geladen …</div>
      ) : (
        GRUPPEN.filter((g) => parameter.some((p) => zuGruppe(p.schluessel) === g)).map((g) => (
        <div className="karte abschnitt" key={g.praefix}>
          <h2>{g.titel}</h2>

          {parameter.filter((p) => zuGruppe(p.schluessel) === g).map((p) => {
            const geaendert = (entwuerfe[p.schluessel] ?? '') !== p.wert
            return (
              <div
                key={p.schluessel}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 190px auto',
                  gap: 14,
                  alignItems: 'start',
                  padding: '14px 0',
                  borderTop: '1px solid var(--grau-200)',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{p.bezeichnung}</div>
                  <div
                    style={{
                      color: 'var(--grau-500)',
                      fontSize: '0.86rem',
                      marginTop: 3,
                      maxWidth: '62ch',
                    }}
                  >
                    {p.beschreibung}
                  </div>
                  <div
                    style={{
                      color: 'var(--grau-500)',
                      fontSize: '0.76rem',
                      marginTop: 4,
                      fontFamily: 'var(--schrift-mono)',
                    }}
                  >
                    {p.schluessel} · Vorgabe: {p.typ === 'JA_NEIN' ? (p.vorgabe === 'true' ? 'ja' : 'nein') : p.vorgabe}
                  </div>
                </div>

                <div>
                  {p.typ === 'JA_NEIN' ? (
                    <select
                      style={{ width: '100%' }}
                      value={entwuerfe[p.schluessel] ?? 'false'}
                      onChange={(e) =>
                        setEntwuerfe({ ...entwuerfe, [p.schluessel]: e.target.value })
                      }
                    >
                      <option value="true">ja</option>
                      <option value="false">nein</option>
                    </select>
                  ) : (
                    <input
                      type={p.typ === 'ZAHL' ? 'number' : 'text'}
                      min={p.minimum}
                      style={{ width: '100%' }}
                      value={entwuerfe[p.schluessel] ?? ''}
                      onChange={(e) =>
                        setEntwuerfe({ ...entwuerfe, [p.schluessel]: e.target.value })
                      }
                    />
                  )}
                </div>

                <button
                  type="button"
                  className={geaendert ? 'knopf-primaer' : 'knopf-schlicht'}
                  disabled={!geaendert}
                  onClick={() => speichern(p)}
                >
                  Speichern
                </button>
              </div>
            )
          })}

          {g.fuss && (
            <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 18 }}>{g.fuss}</p>
          )}
        </div>
        ))
      )}
    </>
  )
}
